import { RunSource } from '../enums/run-source.enum';

export interface ParsedRunInfo {
    source: RunSource;
    baseConfiguration: string | null;
    displayName: string;
    tcDays: number | null;
}

/**
 * Parses a run name to extract source type and base configuration.
 * 
 * Patterns:
 * - Freight: "Freight: ... - FOB-DES broken" or "Freight: ... - FOB-DES broken + India"
 * - Freight Base: "Base - FOB-DES broken + India"
 * - Allegro Case: Contains "_AllegroCase" (e.g., "2026-01-12_173348_BaseCase_AllegroCase")
 * - Opp Cost: Starts with "OppCost_" (e.g., "OppCost_2026-01-12_173348_BaseCase")
 * - Local: Contains "(Local Run)" anywhere in the name
 * - Cloud: Everything else
 */
export function parseRunName(name: string): ParsedRunInfo {
    if (!name) {
        return { source: RunSource.Cloud, baseConfiguration: null, displayName: name, tcDays: null };
    }

    // Extract TC days from pattern like "13d±13d" - use the minimum (first number)
    const tcDays = extractTcDays(name);

    // Check for Local Run
    if (name.includes('(Local Run)')) {
        return {
            source: RunSource.Local,
            baseConfiguration: null,
            displayName: name.replace('(Local Run)', '').trim(),
            tcDays
        };
    }

    // Check for Freight runs: "Freight: ... - FOB-DES broken" or "Freight: ... - FOB-DES broken + India"
    if (name.startsWith('Freight:')) {
        const baseConfig = extractBaseConfigFromFreight(name);
        // Remove "Freight: " prefix for display
        const displayName = name.replace(/^Freight:\s*/, '').trim();
        return {
            source: RunSource.Freight,
            baseConfiguration: baseConfig,
            displayName: displayName,
            tcDays
        };
    }

    // Check for Base runs: "Base - FOB-DES broken + India"
    if (name.startsWith('Base - ') || name.startsWith('Base -')) {
        const baseConfig = name.replace(/^Base\s*-\s*/, '').trim();
        // Remove "Base - " prefix for display, just show the config
        return {
            source: RunSource.FreightBase,
            baseConfiguration: normalizeBaseConfig(baseConfig),
            displayName: baseConfig,
            tcDays
        };
    }

    // Check for Allegro Case runs: contains "_AllegroCase"
    if (name.includes('_AllegroCase')) {
        return {
            source: RunSource.AllegroCase,
            baseConfiguration: null,
            displayName: name,
            tcDays
        };
    }

    // Check for Opp Cost runs: starts with "OppCost_"
    if (name.startsWith('OppCost_')) {
        return {
            source: RunSource.OppCost,
            baseConfiguration: null,
            displayName: name,
            tcDays
        };
    }

    // Default: Cloud
    return { source: RunSource.Cloud, baseConfiguration: null, displayName: name, tcDays };
}

/**
 * Extracts TC days from run name pattern like "13d±13d", "35d±10d", or just "35d".
 * Returns the first number before 'd'.
 */
export function extractTcDays(name: string): number | null {
    if (!name) return null;
    // Pattern: number followed by 'd' optionally with '±' and another number and 'd'
    const match = name.match(/(\d+)d(?:±\d+d)?/i);
    if (match) {
        return parseInt(match[1], 10);
    }
    return null;
}

/**
 * Extracts the base configuration from a Freight run name.
 * E.g., "Freight: IOC 2026-02-27 USA Sabine Pass 35d±10d - 2026-01-05 - FOB-DES broken"
 * Returns: "FOB-DES broken"
 */
function extractBaseConfigFromFreight(name: string): string | null {
    // Look for patterns like "- FOB-DES broken" or "- FOB-DES broken + India" at the end
    const patterns = [
        /- (FOB-DES broken \+ India)\s*$/i,
        /- (FOB-DES broken)\s*$/i
    ];

    for (const pattern of patterns) {
        const match = name.match(pattern);
        if (match) {
            return normalizeBaseConfig(match[1]);
        }
    }

    return null;
}

/**
 * Normalizes base configuration string for comparison.
 * Handles case variations and whitespace.
 */
export function normalizeBaseConfig(config: string | null): string | null {
    if (!config) return null;
    return config.trim().toLowerCase();
}

/**
 * Checks if two base configurations match.
 */
export function baseConfigsMatch(config1: string | null, config2: string | null): boolean {
    const norm1 = normalizeBaseConfig(config1);
    const norm2 = normalizeBaseConfig(config2);
    return norm1 !== null && norm2 !== null && norm1 === norm2;
}
